import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { createAdminSession, destroyAdminSession, validateAdminCredentials } from "./adminSession.js"
import { initRiseConvPage } from "./riseconvLegacy.js"
import { fetchScenarioRequests } from "./scenarioRequests.js"
import { SITE_PAGES } from "./siteContent.js"
import { prepareSiteHtml } from "./siteHtml.js"
import { getPageByPath, normalizeSiteHref } from "./siteRoutes.js"

function readLocation() {
  return `${window.location.pathname}${window.location.hash}`
}

export default function App() {
  const shellRef = useRef(null)
  const [location, setLocation] = useState(readLocation)
  const adminPath = normalizeAdminPath(window.location.pathname)
  const isAdminLogin = adminPath === "/admin/login"
  const isAdminDashboard = adminPath === "/admin"
  const isAdminScenarioRequests = adminPath === "/admin/scenario-requests"
  const isAdminPage = isAdminLogin || isAdminDashboard || isAdminScenarioRequests
  const page = isAdminPage ? null : SITE_PAGES[getPageByPath(location).id] || SITE_PAGES.home
  const html = useMemo(() => (page ? prepareSiteHtml(page.html, page.path) : ""), [page])

  useLayoutEffect(() => {
    document.documentElement.lang = "zh-CN"
    if (isAdminPage) {
      document.title = isAdminLogin ? "后台登录 | RiseConv" : (isAdminDashboard ? "后台 Dashboard | RiseConv" : "访问者需求场景 | RiseConv")
      document.body.className = "admin-scenario-requests-page"
      syncDescription("RiseConv 官网访问者需求场景后台，用于查看访客留下的真实业务场景和联系方式。")
      return
    }

    document.title = page.title
    document.body.className = page.bodyClass || ""
    syncDescription(page.description)
  }, [isAdminScenarioRequests, page])

  useEffect(() => {
    const handlePopState = () => setLocation(readLocation())
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [])

  useEffect(() => {
    const shell = shellRef.current
    if (!shell || isAdminPage) return undefined

    const handleClick = (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const link = event.target.closest?.("a[href]")
      if (!link || !shell.contains(link) || link.target) return

      const normalized = normalizeSiteHref(link.getAttribute("href"), page.path)
      if (!normalized || normalized.startsWith("#")) return

      event.preventDefault()
      navigate(normalized, setLocation)
    }

    shell.addEventListener("click", handleClick)
    return () => shell.removeEventListener("click", handleClick)
  }, [isAdminPage, page?.path])

  useEffect(() => {
    if (isAdminPage) return undefined

    const cleanup = initRiseConvPage()
    scrollToHashOrTop()
    return cleanup
  }, [html, isAdminPage, location])

  if (isAdminLogin) return <AdminLogin />
  if (isAdminPage) return <AdminScenarioRequests dashboard={isAdminDashboard} />

  return <div ref={shellRef} dangerouslySetInnerHTML={{ __html: html }} />
}

function AdminLogin() {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [toast, setToast] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!toast) return undefined

    const timer = window.setTimeout(() => setToast(null), 4200)
    return () => window.clearTimeout(timer)
  }, [toast])

  function showToast(message) {
    setToast({ id: Date.now(), message })
  }

  async function handleSubmit(event) {
    event.preventDefault()
    const validationError = validateAdminCredentials({ username, password })
    if (validationError) {
      showToast(validationError)
      return
    }

    setLoading(true)
    setToast(null)

    try {
      await createAdminSession({ username, password })
      window.location.assign("/admin")
    } catch (loginError) {
      showToast(loginError.message || "登录失败，请稍后再试。")
      setPassword("")
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="admin-login-shell">
      {toast && (
        <div className="admin-login-toast" key={toast.id} role="alert" aria-live="assertive" aria-atomic="true">
          {toast.message}
        </div>
      )}
      <form className="admin-login-panel" onSubmit={handleSubmit}>
        <p className="admin-scenario-kicker">RiseConv Admin</p>
        <h1>后台登录</h1>
        <label>
          <span>管理员账号</span>
          <input autoComplete="username" autoFocus name="username" value={username} onChange={(event) => setUsername(event.target.value)} />
        </label>
        <label>
          <span>管理员密码</span>
          <input autoComplete="current-password" name="password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
        </label>
        <button className="admin-scenario-refresh" type="submit" disabled={loading}>
          {loading ? "登录中" : "登录"}
        </button>
      </form>
    </main>
  )
}

function AdminScenarioRequests({ dashboard = false }) {
  const [requests, setRequests] = useState([])
  const [selected, setSelected] = useState(null)
  const [meta, setMeta] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    loadScenarioRequests()
  }, [])

  async function loadScenarioRequests() {
    setLoading(true)
    setError("")

    try {
      const result = await fetchScenarioRequests()
      setRequests(result.data)
      setMeta(result.meta)
      setSelected((current) => result.data.find((item) => item.id === current?.id) || result.data[0] || null)
    } catch (loadError) {
      setError(loadError.message || "需求场景读取失败，请稍后再试。")
    } finally {
      setLoading(false)
    }
  }

  async function handleSignOut() {
    await destroyAdminSession()
    window.location.assign("/admin/login")
  }

  return (
    <main className="admin-scenario-shell">
      <header className="admin-scenario-topbar">
        <div>
          <p className="admin-scenario-kicker">RiseConv Admin</p>
          <h1>{dashboard ? "后台 Dashboard" : "访问者需求场景"}</h1>
        </div>
        <div className="admin-scenario-actions">
          <nav className="admin-scenario-nav" aria-label="后台导航">
            <a className="is-active" href="/admin/scenario-requests">需求场景</a>
          </nav>
          <button className="admin-scenario-refresh" type="button" onClick={loadScenarioRequests} disabled={loading}>
            {loading ? "读取中" : "刷新"}
          </button>
          <button className="admin-scenario-refresh" type="button" onClick={handleSignOut}>
            退出登录
          </button>
        </div>
      </header>

      {error && <p className="admin-scenario-alert">{error}</p>}

      <section className="admin-scenario-layout" aria-label="访问者需求场景后台">
        <aside className="admin-scenario-list" aria-label="需求场景列表">
          <div className="admin-scenario-list-head">
            <span>场景列表</span>
            <strong>{meta.total_count ?? requests.length}</strong>
          </div>

          {loading && <p className="admin-scenario-empty">正在读取需求场景...</p>}
          {!loading && requests.length === 0 && <p className="admin-scenario-empty">暂时还没有访问者留下需求场景。</p>}
          {!loading && requests.map((request) => (
            <button
              className={`admin-scenario-row${selected?.id === request.id ? " is-active" : ""}`}
              key={request.id}
              type="button"
              onClick={() => setSelected(request)}
            >
              <span>{request.scene || "未选择卡点类型"}</span>
              <strong>{request.contact || "未留联系方式"}</strong>
              <time dateTime={request.created_at}>{formatDate(request.created_at)}</time>
            </button>
          ))}
        </aside>

        <article className="admin-scenario-detail" aria-label="需求场景详情">
          {selected ? <ScenarioRequestDetail request={selected} /> : <p className="admin-scenario-empty">选择左侧需求场景后查看详情。</p>}
        </article>
      </section>
    </main>
  )
}

function normalizeAdminPath(path) {
  return path.replace(/\/+$/, "") || "/"
}

function ScenarioRequestDetail({ request }) {
  return (
    <>
      <div className="admin-scenario-detail-head">
        <div>
          <p className="admin-scenario-kicker">Scene Request</p>
          <h2>{request.scene || "未选择卡点类型"}</h2>
        </div>
        <time dateTime={request.created_at}>{formatDate(request.created_at)}</time>
      </div>

      <dl className="admin-scenario-fields">
        <DetailField label="现场描述" value={request.problem} wide />
        <DetailField label="联系方式" value={request.contact} />
        <DetailField label="已有材料" value={request.assets} />
        <DetailField label="组织线索" value={request.company} />
        <DetailField label="行业或部门" value={request.industry} />
        <DetailField label="推进时间" value={request.timeline} />
        <DetailField label="页面路径" value={request.page_path} />
        <DetailField label="来源页面" value={request.referer} />
        <DetailField label="IP 地址" value={request.ip_address} />
        <DetailField label="浏览器" value={request.user_agent} wide />
      </dl>
    </>
  )
}

function DetailField({ label, value, wide = false }) {
  return (
    <div className={wide ? "is-wide" : ""}>
      <dt>{label}</dt>
      <dd>{value || "未填写"}</dd>
    </div>
  )
}

function formatDate(value) {
  if (!value) return "时间未知"

  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value))
}

function syncDescription(description) {
  let meta = document.querySelector('meta[name="description"]')
  if (!meta) {
    meta = document.createElement("meta")
    meta.setAttribute("name", "description")
    document.head.append(meta)
  }

  meta.setAttribute("content", description || "")
}

function navigate(target, setLocation) {
  const current = readLocation()
  if (target !== current) {
    window.history.pushState({}, "", target)
    setLocation(readLocation())
    return
  }

  scrollToHashOrTop()
}

function scrollToHashOrTop() {
  window.requestAnimationFrame(() => {
    const hash = window.location.hash.slice(1)
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" })
      return
    }

    document.getElementById(decodeURIComponent(hash))?.scrollIntoView({ block: "start" })
  })
}
